import {PlanTypes} from "../types";

/**
 * Konfiguracija planova prikazanih na /subscription stranici.
 *
 * Ovde se menjaju SVI tekstovi kartica — naslov, cena-sufiks (subtitle),
 * opis i stavke (bullets). Cene se ne unose ovde; dolaze sa API-ja preko
 * pricesMap[subscriptionType] u Subscription.tsx.
 *
 * NAPOMENA: poredbene tvrdnje ("niža cena po mesecu" itd.) prate trenutni
 * raspored cena — ako se cene menjaju, proveri da li i dalje važe.
 */

export interface PlanItem {
  checked: boolean;
  text: string;
}

export interface PlanConfig {
  title: string;
  subtitle: string;
  subscriptionType: PlanTypes;
  isOneTime: boolean;
  description: string;
  items: PlanItem[];
  discount?: number;
}

// Svi planovi otključavaju isti sadržaj - sažeto u jednu stavku da se ne ponavlja po karticama.
export const FULL_CONTENT_ITEM: PlanItem = {
  checked: true,
  text: "Ceo sadržaj: Neurobiologija, Emisije, Predavanja, Meditacije",
};

export const COMMUNITY_ACCESS_ITEM: PlanItem = {
  checked: true,
  text: "Pristup NeoUm zajednici za podršku",
};

// ——— PRETPLATA (automatsko obnavljanje) ———
export const SUBSCRIPTION_PLANS: PlanConfig[] = [
  {
    title: "Mesečni",
    subtitle: "30 dana",
    subscriptionType: PlanTypes.Monthly,
    isOneTime: false,
    description:
      "Fleksibilan pristup koji se sam obnavlja svakog meseca. Otključaj ceo sadržaj odmah i otkaži kad god poželiš - bez ugovora i obaveza.",
    items: [
      {checked: true, text: "Automatsko obnavljanje, otkaži bilo kada"},
      FULL_CONTENT_ITEM,
      COMMUNITY_ACCESS_ITEM,
    ],
  },
  {
    title: "Šestomesečni",
    subtitle: "6 meseci",
    subscriptionType: PlanTypes.SixMonths,
    isOneTime: false,
    description:
      "Pretplata na šest meseci koja se automatski obnavlja svakih pola godine. Duži kontinuitet uz nižu cenu po mesecu, a otkažeš kad god poželiš.",
    items: [
      {checked: true, text: "Automatsko obnavljanje, otkaži bilo kada"},
      FULL_CONTENT_ITEM,
      COMMUNITY_ACCESS_ITEM,
    ],
  },
  {
    title: "Godišnji",
    subtitle: "12 meseci",
    subscriptionType: PlanTypes.Yearly,
    isOneTime: false,
    description:
      "Godišnja pretplata koja se automatski obnavlja jednom godišnje. Najisplativiji pristup po mesecu za dugoročan rad, uz mogućnost otkazivanja u svakom trenutku.",
    items: [
      {checked: true, text: "Automatsko obnavljanje, otkaži bilo kada"},
      FULL_CONTENT_ITEM,
      COMMUNITY_ACCESS_ITEM,
    ],
  },
];

// ——— JEDNOKRATNO PLAĆANJE (bez automatskog obnavljanja) ———
export const ONE_TIME_PLANS: PlanConfig[] = [
  {
    title: "1 mesec",
    subtitle: "30 dana",
    subscriptionType: PlanTypes.OneTimeOneMonth,
    isOneTime: true,
    description:
      "Savršeno za prvi susret sa platformom. Otključaj sve na mesec dana, potpuno bez obaveza kada period istekne.",
    items: [
      {checked: true, text: "Pristup 30 dana"},
      {checked: true, text: "Bez automatskog obnavljanja"},
      FULL_CONTENT_ITEM,
      COMMUNITY_ACCESS_ITEM,
    ],
  },
  {
    title: "3 meseca",
    subtitle: "90 dana",
    subscriptionType: PlanTypes.OneTimeThreeMonths,
    isOneTime: true,
    discount: 10,
    description:
      "Za one koji žele kontinuitet. Tri meseca su dovoljna da kreneš graditi zdrave navike za promenu koju želiš - uz nižu cenu po mesecu nego kod mesečne uplate.",
    items: [
      {checked: true, text: "Pristup 90 dana"},
      {checked: true, text: "Niža cena po mesecu"},
      FULL_CONTENT_ITEM,
      COMMUNITY_ACCESS_ITEM,
    ],
  },
  {
    title: "6 meseci",
    subtitle: "180 dana",
    subscriptionType: PlanTypes.OneTimeSixMonths,
    isOneTime: true,
    discount: 19,
    description:
      "Pola godine posvećenog rada kroz sve sadržaje platforme, po znatno povoljnijoj ceni za duži period.",
    items: [
      {checked: true, text: "Pristup 180 dana"},
      {checked: true, text: "Povoljnije za duži period"},
      FULL_CONTENT_ITEM,
      COMMUNITY_ACCESS_ITEM,
    ],
  },
  {
    title: "12 meseci",
    subtitle: "365 dana",
    subscriptionType: PlanTypes.OneTimeYear,
    isOneTime: true,
    discount: 38,
    description:
      "Cela godina pristupa po najisplativijoj ceni po mesecu. Za one koji ozbiljno ulažu u sebe, bez brige o obnavljanju.",
    items: [
      {checked: true, text: "Pristup 365 dana"},
      {checked: true, text: "Najbolja cena po mesecu"},
      FULL_CONTENT_ITEM,
      COMMUNITY_ACCESS_ITEM,
    ],
  },
];

export const isOneTimePlanType = (type?: string | null) =>
  !!type && ONE_TIME_PLANS.some((plan) => plan.subscriptionType === type);

export const isRecurringPlanType = (type?: string | null) =>
  !!type && SUBSCRIPTION_PLANS.some((plan) => plan.subscriptionType === type);
